/**
 * Parsing for values that are calendar dates rather than moments in time. A date of birth is one:
 * it is the same day everywhere, and has no time or timezone of its own.
 *
 * `new Date('1950-05-14')` is midnight UTC. Reading that back through the local getters that
 * p-calendar and `formatDob` use gives 13 May anywhere behind UTC, so an agency user in the US
 * sees, sends and stores a date of birth one day early. `new Date('05/14/1950')` is midnight
 * local and comes back correctly, and patient records hold both forms — the bulk upload keeps
 * `YYYY-MM-DD` as it finds it and rewrites anything else to `MM/DD/YYYY`. The shift therefore
 * affects some patients and not others, which makes it easy to miss.
 *
 * Both forms are parsed here to local midnight, which is the basis the local getters expect.
 */
export function parseDateOnly(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const text = String(value).trim();
  if (!text) {
    return null;
  }

  // 1950-05-14, optionally carrying a time that we deliberately ignore: the calendar date is
  // whatever the record says, not whatever that instant lands on in the reader's timezone.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/.exec(text);
  if (iso) {
    return buildLocalDate(+iso[1], +iso[2], +iso[3]);
  }

  // 05/14/1950 and 05-14-1950
  const us = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(text);
  if (us) {
    return buildLocalDate(+us[3], +us[1], +us[2]);
  }

  // Anything else is left to the platform, which is no worse than before.
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildLocalDate(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  // Reject a date the calendar rolled over, such as 31 February.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}
